import {EventOption} from 'airbridge-web-sdk-loader/build/airbridge_api';
import {useEffect} from 'react';
import {generatePath, useNavigate, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {
  useChangeCardInfo, useExecuteOnetimePayment, useExecutePayment, useExecuteTrial,
  useIssueBillingKey
} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {DottedLoadingBar} from '@/components/Loading';
import {Typography} from '@/components/Typography';
import {PaymentActionType, PaymentProcess, ProductTier} from '@/enums';
import {useAirbridgeTracking, useGA4WithParams} from '@/hooks/tracking';
import {
  ExecuteOnetimePaymentRecord, IssueBillingKeyRecord, PaymentProcessParams
} from '@/interface';
import {buildSearchParam, paths} from '@/router';
import {PaddingBox, PageContent} from '@/styled-components';
import {palette} from '@/styles';

export const Inprogress = ({transaction, type}: PaymentProcessParams) => {
  const [searchParams] = useSearchParams();
  const {mutate: issueBillingKey} = useIssueBillingKey();
  const {mutate: executePayment} = useExecutePayment();
  const {mutate: executeTrial} = useExecuteTrial();
  const {mutate: excuteOnetimePayment} = useExecuteOnetimePayment();
  const {mutate: changeCardInfo} = useChangeCardInfo();

  const navigate = useNavigate();
  const tracking = useGA4WithParams();
  const trackAirbridge = useAirbridgeTracking();

  const getAirbridgeAttrs = () => {
    const eventAttribute: EventOption = {};

    if (searchParams) {
      const name = searchParams.get('name');
      const discount = searchParams.get('discount');
      const amount = searchParams.get('amount');

      if (name) {
        eventAttribute.action = name;
      }
      if (discount) {
        eventAttribute.label = discount;
      }
      if (amount && !isNaN(Number(amount))) {
        eventAttribute.value = Number(amount);
        eventAttribute.semanticAttributes = {
          currency: 'KRW',
        };
      }
    }

    return eventAttribute;
  };

  const redirectFailurePage = (error: Error) => {
    // 이용 제한은 실패 화면 대신 결제를 시작한 화면으로 돌려보낸다 (안내 모달은 공통 에러 핸들러가 띄운다).
    if (isAccessRestrictionError(error)) {
      navigate(
        {
          pathname:
            type === PaymentActionType.method
              ? paths.mypage_pay
              : paths.pricing_root,
        },
        {replace: true}
      );
      return;
    }

    navigate(
      {
        pathname: generatePath(paths.payments_process, {
          type,
          transaction,
          status: PaymentProcess.fail,
        }),
        search: `message=${getGqlErrorMessage(error, false)}`,
      },
      {replace: true}
    );
  };

  const redirectSuccessPage = (params?: {[key: string]: any}) => {
    navigate(
      {
        pathname: generatePath(paths.payments_process, {
          type,
          transaction,
          status: PaymentProcess.success,
        }),
        search: params ? buildSearchParam(params) : undefined,
      },
      {replace: true}
    );
  };

  const requestSubscription = (record: IssueBillingKeyRecord) => {
    issueBillingKey(record, {
      onSuccess: (data) => {
        if (data.result?.transactionId) {
          (searchParams.get('tier') === ProductTier.Trial
            ? executeTrial
            : executePayment)(
            {
              transactionId: data.result.transactionId,
              customerKey: data.result.customerKey,
            },
            {
              onSuccess: (data) => {
                if (data?.result?.tier) {
                  tracking(
                    data?.result?.tier === ProductTier.Trial
                      ? 'trial_start'
                      : 'subscribe'
                  );

                  trackAirbridge('airbridge.subscribe', getAirbridgeAttrs());

                  redirectSuccessPage({
                    tier: data.result.tier,
                  });
                }
              },
              onError: (error) => {
                redirectFailurePage(error);
              },
            }
          );
        }
      },
      onError: (error) => {
        redirectFailurePage(error);
      },
    });
  };

  const requestOnetimePayment = (record: ExecuteOnetimePaymentRecord) => {
    excuteOnetimePayment(record, {
      onSuccess: (data) => {
        if (data?.result?.tier) {
          const productAttrs = getAirbridgeAttrs();

          tracking('order_completed', {
            product_name: productAttrs.action,
          });

          trackAirbridge('airbridge.ecommerce.order.completed', productAttrs);

          redirectSuccessPage({
            tier: data.result.tier,
          });
        }
      },
      onError: (error) => {
        redirectFailurePage(error);
      },
    });
  };

  useEffect(() => {
    if (searchParams && transaction) {
      const customerKey = searchParams.get('customerKey');

      if (type === PaymentActionType.onetime) {
        const paymentKey = searchParams.get('paymentKey');
        const amount = searchParams.get('amount');

        if (paymentKey && amount && customerKey) {
          requestOnetimePayment({
            customerKey,
            paymentKey,
            transactionId: transaction,
            amount: Number(amount),
          });
        }
      } else {
        const authKey = searchParams.get('authKey');

        if (authKey && customerKey) {
          const record = {
            authKey,
            customerKey,
            transactionId: transaction,
          };

          if (type === PaymentActionType.method) {
            changeCardInfo(record, {
              onSuccess: (data) => {
                if (data?.result?.transactionId) {
                  redirectSuccessPage();
                }
              },
              onError: (error) => {
                redirectFailurePage(error);
              },
            });
          } else {
            requestSubscription(record);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Root>
      <DottedLoadingBar />
      <PaddingBox top={40}>
        <Typography variant="bold24" color={palette.gray900}>
          {type === PaymentActionType.method
            ? '카드 정보를 변경 중입니다'
            : '결제가 진행중입니다'}
        </Typography>
      </PaddingBox>
      <PaddingBox top={16}>
        <Typography variant="medium14" color={palette.gray600}>
          잠시만 기다려주세요
        </Typography>
      </PaddingBox>
    </Root>
  );
};

const Root = styled(PageContent)`
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
`;
